import { z } from 'zod';
import {
  dunningLevels,
  interestTypes,
  memberRoles,
  paymentMethods,
  practitionerTypes,
  vatModes,
  type DunningLevel,
  type MemberRole,
  type PaymentMethod,
  type PractitionerType,
  type VatMode,
} from '../enums.js';
import { optionalText } from './_fields.js';
import { bicFieldSchema, ibanFieldSchema } from '../validation/iban.js';
import { basisPointsSchema, nonNegativeCentsSchema } from './_billing-fields.js';
import type { CreditNoteDto, InvoiceDto, InvoiceLineDto, PaymentDto } from './billing-invoices.js';
import type { DunningNoticeDto } from './billing-reminders.js';
import type { LateInterestDto } from './billing-late-interests.js';
import type { PaymentOrderDto } from './billing-payment-orders.js';
import { dateOnlySchema } from './patients.js';

export * from './_billing-fields.js';
export * from './billing-invoices.js';
export * from './billing-late-interests.js';
export * from './billing-reminders.js';
export * from './billing-dunning-run.js';
export * from './billing-payment-orders.js';

/**
 * Contrat réseau des réglages de facturation du cabinet, des conditions d'un membre et de l'état
 * de rétrocession.
 *
 * Le régime de TVA vit ici et non sur `organizations` : c'est un paramètre comptable, il se fige
 * à la première facture émise, et `docs/BACKLOG.md` laisse la décision « exonération ou 17 % »
 * ouverte. Les deux modes sont donc stockables et aucun n'est imposé par le code.
 */

/* -------------------------------------------------------------------------- */
/* Réglages du cabinet                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Réglage d'un rappel. `delayDays` se compte depuis l'échéance pour le premier rappel, depuis
 * l'envoi du rappel précédent pour les suivants. `interestValue` est en **points de base** pour
 * `percentage` et `annual_rate` (375 = 3,75 %), en **centimes** pour `fixed`, et vaut 0 pour
 * `none` : des entiers, aucun flottant.
 *
 * Le nombre de rappels actifs n'est pas ici mais dans `reminderCount` : les rappels se suivent
 * sans trou, un interrupteur par niveau permettait d'éteindre le deuxième et d'allumer le
 * troisième, une politique qui ne relançait plus jamais.
 */
export const dunningLevelConfigSchema = z
  .object({
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    delayDays: z.number().int().min(0).max(365),
    interestType: z.enum(interestTypes),
    interestValue: nonNegativeCentsSchema,
  })
  .strict()
  .refine(
    (entry) =>
      (entry.interestType !== 'percentage' && entry.interestType !== 'annual_rate') ||
      basisPointsSchema.safeParse(entry.interestValue).success,
    { message: 'Un taux s’exprime entre 0 et 100 %', path: ['interestValue'] },
  );
export type DunningLevelConfig = z.infer<typeof dunningLevelConfigSchema>;

export const DEFAULT_DUNNING_DELAY_DAYS = 30;
export const DEFAULT_PAYMENT_TERMS_DAYS = 30;
/** Par défaut, deux rappels avant la requête en ordonnance de paiement. */
export const DEFAULT_REMINDER_COUNT = 2;
export const DEFAULT_PAYMENT_ORDER_DELAY_DAYS = 30;

/** Politique par défaut : trente jours entre chaque étape, aucun intérêt tant que le cabinet n'en fixe pas. */
export const defaultDunningLevels: readonly DunningLevelConfig[] = dunningLevels.map((level) => ({
  level,
  delayDays: DEFAULT_DUNNING_DELAY_DAYS,
  interestType: 'none',
  interestValue: 0,
}));

/**
 * Seuil luxembourgeois au-delà duquel un cabinet exonéré doit passer au taux standard : 50 000 €
 * d'actes **hors ostéopathie** sur l'année civile. Les soins d'ostéopathie, eux, restent exonérés
 * quel que soit leur montant.
 *
 * En centimes entiers, comme tout montant de ce dépôt. C'est un paramètre nommé une fois et non
 * une constante recopiée dans un service, une route et un écran.
 */
export const VAT_EXEMPTION_THRESHOLD_CENTS = 5_000_000;

/**
 * Surveillance du seuil pour une année civile.
 *
 * **L'application avertit, elle ne bascule jamais seule** (arbitrage du 20 septembre 2026). Changer
 * le régime de TVA d'un cabinet est une décision fiscale qui engage son comptable, et le régime se
 * verrouille de toute façon dès la première facture émise.
 *
 * Le cumul ne compte que les pièces **émises** — un brouillon n'est pas une pièce comptable — et
 * les avoirs émis viennent en déduction : annuler une vente ne doit pas laisser le cabinet
 * franchir un seuil sur un chiffre d'affaires qu'il n'a pas fait. Il peut donc être négatif si un
 * avoir porte sur une année antérieure, et l'écran le montre tel quel plutôt que de le tronquer à
 * zéro : un cumul négatif est une information, pas une erreur d'affichage.
 */
export interface VatThresholdDto {
  /** Année civile observée, celle de la date d'émission des pièces. */
  year: number;
  /** Cumul des actes hors ostéopathie, avoirs déduits. */
  outsideOsteopathyCents: number;
  thresholdCents: number;
  /** Vrai dès que le cumul dépasse le seuil. C'est ce qui déclenche l'avertissement. */
  exceeded: boolean;
  /**
   * Faux quand le cabinet est déjà au taux standard : il n'a plus de seuil à franchir, et la
   * surveillance n'a plus d'objet. L'écran masque alors l'avertissement au lieu de l'afficher
   * éteint.
   */
  watched: boolean;
}

/** `PUT` : les réglages sont remplacés en entier, un champ absent serait ambigu. */
export const billingSettingsUpdateSchema = z
  .object({
    vatMode: z.enum(vatModes),
    paymentTermsDays: z.number().int().min(0).max(365),
    defaultPaymentMethods: z.array(z.enum(paymentMethods)).max(paymentMethods.length),
    dunningEnabled: z.boolean(),
    /**
     * Envoyé par l'assistant et Paramètres → Relances : le cabinet vient de trancher, oui ou non.
     * Le serveur date lui-même la décision ; Paramètres → Facturation ne l'envoie pas.
     */
    dunningDecided: z.literal(true).optional(),
    dunningLevels: z
      .array(dunningLevelConfigSchema)
      .length(3)
      .refine(
        (levels) => levels.every((entry, index) => entry.level === index + 1),
        'Les trois niveaux doivent être donnés dans l’ordre 1, 2, 3',
      ),
    /** Nombre de rappels envoyés avant la requête en ordonnance de paiement : 1, 2 ou 3. */
    reminderCount: z.number().int().min(1).max(3),
    paymentOrderEnabled: z.boolean(),
    /** Jours entre l'envoi du dernier rappel et la préparation de la requête. */
    paymentOrderDelayDays: z.number().int().min(0).max(365),
    invoiceFooter: optionalText(2_000),
    /** Compte du cabinet imprimé sur la facture : IBAN vérifié (mod 97), vide = aucun. */
    iban: ibanFieldSchema,
    /** Facultatif, même quand l'IBAN est donné. */
    bic: bicFieldSchema,
  })
  .strict();
export type BillingSettingsUpdateInput = z.infer<typeof billingSettingsUpdateSchema>;

export interface BillingSettingsDto {
  vatMode: VatMode;
  paymentTermsDays: number;
  /**
   * Moyens cochés dans Paramètres → Facturation. Peut encore contenir `wero` ou `payconiq` :
   * on ne réécrit pas la colonne. À la lecture, ces deux valeurs ne sont pas des cases. Wero
   * proposé au patient vient de `weroAccepted`.
   */
  defaultPaymentMethods: PaymentMethod[];
  /**
   * Vrai quand l'interrupteur de Paramètres → Wero est allumé. Une ancienne case `wero` dans
   * `defaultPaymentMethods` ne suffit pas : l'intégration éteinte laisse Wero éteint.
   */
  weroAccepted: boolean;
  dunningEnabled: boolean;
  dunningLevels: DunningLevelConfig[];
  reminderCount: number;
  paymentOrderEnabled: boolean;
  paymentOrderDelayDays: number;
  invoiceFooter: string | null;
  /** Normalisé, sans espaces ; l'écran et le PDF le groupent par quatre (`formatIban`). */
  iban: string | null;
  bic: string | null;
  /** Vrai dès qu'une facture a été émise : le régime de TVA ne bouge plus (409 `vat_mode_locked`). */
  vatModeLocked: boolean;
  /**
   * Surveillance du seuil pour l'année civile en cours. Rendue avec les réglages parce que c'est
   * le même écran qui porte le régime de TVA et l'avertissement qui invite à en changer.
   */
  vatThreshold: VatThresholdDto;
}

/* -------------------------------------------------------------------------- */
/* Conditions d'un membre et rétrocession                                      */
/* -------------------------------------------------------------------------- */

/**
 * Période de conditions d'un membre : rôle, taux, et date de début.
 * `docs/BACKLOG.md` « Historique temporel des rôles et taux » : le rôle porté par
 * `organization_members` est un miroir de la période en cours, la vérité comptable est ici, sans
 * quoi une facture ancienne serait rétrocédée au taux d'aujourd'hui.
 *
 * Pas de date de fin : le service la déduit (veille du début suivant, nulle pour la dernière).
 * Un corps qui envoie `validTo` est refusé.
 */
export const memberTermsInputSchema = z
  .object({
    role: z.enum(memberRoles),
    retrocessionRateBps: basisPointsSchema,
    /**
     * Nul ou absent : taux global, pour tout type sans taux propre.
     * Renseigné : taux de ce type de consultation seulement. Il ne remplace pas le global.
     */
    consultationTypeId: z.uuid().nullable().optional(),
    validFrom: dateOnlySchema,
  })
  .strict();
export type MemberTermsInput = z.infer<typeof memberTermsInputSchema>;

export interface MemberTermsDto {
  id: string;
  memberId: string;
  role: MemberRole;
  retrocessionRateBps: number;
  /** Nul : taux global. Renseigné : ce taux prime pour ce type. */
  consultationTypeId: string | null;
  validFrom: string;
  /**
   * Veille du début suivant, ou nul pour la dernière période du périmètre.
   * Calculé à l'écriture : le corps de la requête ne le porte pas.
   */
  validTo: string | null;
  /**
   * Vrai quand une facture émise du praticien tombe dans la période. Le taux de ce jour-là est
   * alors figé : on le fait varier en ouvrant une période suivante, on ne réécrit pas celle-ci.
   */
  locked: boolean;
  createdAt: string;
}

export const memberTermsIdParamSchema = z
  .object({ memberId: z.uuid(), termsId: z.uuid() })
  .strict();
export type MemberTermsIdParam = z.infer<typeof memberTermsIdParamSchema>;

/** Place d'une période par rapport au jour civil du cabinet. */
export const retrocessionPeriodStatuses = ['past', 'current', 'future'] as const;
export type RetrocessionPeriodStatus = (typeof retrocessionPeriodStatuses)[number];

/** Une période de taux, telle que l'écran des pourcentages la montre. */
export interface RetrocessionRatePeriodDto {
  id: string;
  role: MemberRole;
  retrocessionRateBps: number;
  /** Nul : période du taux global. */
  consultationTypeId: string | null;
  /** Libellé du type, quand la période n'est pas globale. */
  consultationTypeName: string | null;
  validFrom: string;
  validTo: string | null;
  locked: boolean;
  status: RetrocessionPeriodStatus;
}

/** Taux d'un type de consultation en vigueur aujourd'hui. Il prime sur le global pour ce type. */
export interface RetrocessionSpecificRateDto {
  consultationTypeId: string;
  name: string;
  rateBps: number;
}

/**
 * Un praticien éligible et ses pourcentages. Le titulaire et le secrétariat n'y figurent pas :
 * l'un encaisse pour lui-même, l'autre ne facture pas (`retrocessionModelByRole`).
 */
export interface RetrocessionPractitionerRatesDto {
  memberId: string;
  firstName: string;
  lastName: string;
  role: MemberRole;
  isActive: boolean;
  /**
   * Taux global qui couvre aujourd'hui. `null` si aucun n'est posé ce jour-là.
   * Les types qui ont leur propre taux sont dans `specificRates`.
   */
  currentRateBps: number | null;
  /**
   * Salarié sans période globale à aujourd'hui : le rapport lui applique 0 % reversé sur les
   * types sans taux propre, soit la totalité de ces honoraires au titulaire. Ce n'est pas un taux saisi.
   */
  usesEmployeeDefault: boolean;
  /** Taux de types en vigueur aujourd'hui. Chacun prime sur le global pour son type. */
  specificRates: RetrocessionSpecificRateDto[];
  periods: RetrocessionRatePeriodDto[];
}

export const retrocessionQuerySchema = z
  .object({ from: dateOnlySchema, to: dateOnlySchema, memberId: z.uuid().optional() })
  .strict()
  .refine((value) => value.to >= value.from, 'La fin de période précède son début');
export type RetrocessionQuery = z.infer<typeof retrocessionQuerySchema>;

/** Ligne de rétrocession d'un membre sur la période, calculée à la volée depuis les encaissements. */
export interface RetrocessionLineDto {
  memberId: string;
  role: MemberRole;
  model: PractitionerType;
  /** `to_owner` : le praticien verse au titulaire. `from_owner` : le titulaire reverse. */
  direction: 'to_owner' | 'from_owner';
  retrocessionRateBps: number;
  /** Encaissements de la période attribués à ce membre, en centimes. */
  collectedCents: number;
  practitionerCents: number;
  ownerCents: number;
  retrocessionCents: number;
}

export interface RetrocessionReportDto {
  from: string;
  to: string;
  lines: RetrocessionLineDto[];
}

/** Les trois modèles, exposés pour l'écran : le sens du flux ne dépend que du modèle. */
export const retrocessionDirections: Readonly<Record<PractitionerType, 'to_owner' | 'from_owner'>> =
  {
    collaborator: 'to_owner',
    substitute: 'from_owner',
    employee: 'from_owner',
  };

export const practitionerTypeSchema = z.enum(practitionerTypes);
export type { DunningLevel };

/**
 * Détail d'une facture : tout ce que le dialogue de détail affiche, en un seul appel. La V1
 * faisait une requête par facture rien que pour compter les documents liés.
 */
export interface InvoiceDetailDto extends InvoiceDto {
  lines: InvoiceLineDto[];
  payments: PaymentDto[];
  creditNotes: CreditNoteDto[];
  dunningNotices: DunningNoticeDto[];
  paymentOrders: PaymentOrderDto[];
  /** Toutes les créances d'intérêts de la facture, la plus récente d'abord. */
  lateInterests: LateInterestDto[];
  /** Intérêts encore dus, ceux de la créance ouverte ; zéro sans relance ou après abandon. */
  interestDueCents: number;
  /**
   * Vrai quand cette facture porte le plus grand numéro de la série du cabinet.
   * Un brouillon, sans numéro, est faux. Une facture annulée peut rester vraie : son numéro
   * n'est pas réattribué. Le bouton d'annulation exige en plus l'absence de paiement.
   */
  isLastIssued: boolean;
}

/**
 * Le praticien **tel qu'il était à l'émission**, figé sur la facture comme le sont déjà les
 * mentions légales et le mode de TVA.
 *
 * Une facture est une pièce comptable : elle doit se réimprimer telle qu'elle a été émise, quoi
 * qu'il soit arrivé depuis à son praticien — titre changé, numéro d'autorisation corrigé, compte
 * effacé. Sans cet instantané, une réimpression forcée relisait le profil vivant et produisait un
 * document qui n'était plus celui qu'on avait remis au patient.
 *
 * La signature n'y est pas : c'est un fichier, pas un champ, et un instantané de fichier n'a de
 * sens que s'il est lui-même figé — ce que le PDF déjà rangé au dossier fait très bien.
 */
export interface PractitionerSnapshot {
  firstName: string;
  lastName: string;
  title: string | null;
  authorizationNumber: string | null;
}
